import { getUserLocation } from "@api/visitors";
import { TGeoLocation } from "@components/types";
import { message } from "antd";
import Cookies from "js-cookie";

// High-accuracy (GPS) positioning frequently comes back as
// POSITION_UNAVAILABLE (code 2) on hardware without a GPS chip — most
// laptops/desktops, and some phones indoors. We try it first for a
// precise fix, then fall back to network/Wi-Fi based positioning
// (enableHighAccuracy: false), which is far more reliable even if less
// precise — plenty good enough for picking a delivery address.
const highAccuracyOptions: PositionOptions = {
    timeout: 8 * 1000,
    enableHighAccuracy: true,
    maximumAge: 0,
};

const lowAccuracyOptions: PositionOptions = {
    timeout: 10 * 1000,
    enableHighAccuracy: false,
    maximumAge: 0,
};

let defaultLocation = { "last_updated": 0, "latitude": 0, "longitude": 0 };

let setCityAndState = function (position: GeolocationPosition) {
    let location = getUserLocation(position?.coords?.longitude, position?.coords?.latitude).then(
        (currentLocation) => {
            if (currentLocation?.city?.name && currentLocation?.state?.name) {
                const myLocation = {
                    Coords: { latitude: position?.coords?.latitude || 27.504, longitude: position?.coords?.longitude || 77.669 },
                    Location: {
                        name: `${currentLocation?.city?.name}, ${currentLocation?.state?.name}`,
                        city_id: currentLocation?.city?.id,
                        state_id: currentLocation?.state?.id,
                        state_name: currentLocation?.state?.name,
                        city_name: currentLocation?.city?.name,
                    },

                };
                localStorage.setItem('location', JSON.stringify(myLocation));
                Cookies.set('location', JSON.stringify(myLocation));
                Cookies.set('longitude', JSON.stringify(position?.coords?.longitude));
                Cookies.set('latitude', JSON.stringify(position?.coords?.latitude));
            }
        }
    );
    return location
}

let geoSuccess = function (position: GeolocationPosition) {
    let updatedLocation = { "last_updated": Date.now(), "latitude": position.coords.latitude, "longitude": position.coords.longitude, "code": 0 }
    setCityAndState(position);
    localStorage.setItem("geoposition", JSON.stringify(updatedLocation));
    return updatedLocation
};

// error.code can be:
//   1: permission denied
//   2: position unavailable (no GPS/network fix could be obtained)
//   3: timed out
export const geoErrorMessage = (code: number): string => {
    switch (code) {
        case 1:
            return 'Location access is blocked. Please allow it in your browser settings.';
        case 3:
            return "Couldn't get your location in time. Please try again.";
        case 2:
        default:
            return "Couldn't detect your location. Check that location services are turned on for this device.";
    }
};

let geoError = function (error: any) {
    if (error?.code !== 1 && error?.message) {
        message?.error(geoErrorMessage(error?.code));
    }
    if (localStorage.getItem("geoposition") === null) {
        localStorage.setItem("geoposition", JSON.stringify({ ...defaultLocation, 'code': error?.code }));
    }
};

const getCurrentPositionAsync = (options: PositionOptions): Promise<GeolocationPosition | GeolocationPositionError> => {
    return new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            position => { resolve(position) },
            error => { reject(error) },
            options
        )
    }).catch(error => error)
}

export const getLocationPromise = async (): Promise<GeolocationPosition | GeolocationPositionError> => {
    const attempt = await getCurrentPositionAsync(highAccuracyOptions);
    // A GPS-only failure (unavailable/timeout) is common on devices without a
    // GPS chip — retry once with network-based positioning before giving up.
    // A permission denial (code 1) won't succeed on retry, so skip it.
    if ("code" in attempt && attempt.code !== 1) {
        return getCurrentPositionAsync(lowAccuracyOptions);
    }
    return attempt;
}

// A cached fix goes stale as the user moves; anything older than this gets
// refreshed so distance-to-store checks aren't computed against an old position.
const LOCATION_MAX_AGE_MS = 5 * 60 * 1000;

export const getLocation = async (override: boolean = false): Promise<TGeoLocation> => {
    let lastKnown: TGeoLocation | null = null;
    if (localStorage.getItem("geoposition") != undefined) {
        let location = JSON.parse(localStorage.getItem("geoposition") || "{}")
        if (location?.latitude > 0) {
            lastKnown = location;
            if (!override && Date.now() - (location?.last_updated || 0) < LOCATION_MAX_AGE_MS) {
                return location;
            }
        }
    }
    const location = await getLocationPromise();
    if ("code" in location) {
        geoError(location);
        // A stale fix beats resetting to 0,0 for distance estimates
        return lastKnown || JSON.parse(localStorage.getItem("geoposition"));
    }
    return geoSuccess(location);
}

// A location result only represents an actual successful fix when it carries
// real, non-zero coordinates — `code` alone isn't enough: many call sites used
// to check `code === 1` (permission denied) and silently treated position-
// unavailable (2) or timeout (3) failures as success, saving 0,0 as if it
// were the user's real location.
export const isValidLocation = (location: TGeoLocation): boolean =>
    !!location && location.latitude > 0 && location.longitude > 0;

// getLocation() falls back to a stale cached position when a fresh fix fails (code 2); that one
// shouldn't be treated as where the user is right now.
export const isFreshLocation = (location: TGeoLocation): boolean =>
    isValidLocation(location) && !(Date.now() - location.last_updated > 60000 && location.code == 2);
